import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Package } from "lucide-react";
import { money } from "@/lib/helpers";
import logoWhite from "@/assets/logo-white.webp";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function Stat({ label, value, accent }) {
  return (
    <div>
      <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-white/60">{label}</p>
      <p className={`mt-0.5 font-heading text-lg font-bold ${accent || "text-white"}`}>{value}</p>
    </div>
  );
}

function ProductCard({ p, token }) {
  const imgUrl = p.has_image ? `${API}/public/product-shares/${token}/image/${p.id}` : null;
  const marginNeg = p.margin != null && p.margin < 0;
  return (
    <div className="group relative aspect-[3/4] overflow-hidden rounded-2xl bg-zinc-800 shadow-xl ring-1 ring-white/10 transition-transform duration-300 hover:-translate-y-1" data-testid={`portfolio-card-${p.id}`}>
      {imgUrl ? (
        <img src={imgUrl} alt={p.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-zinc-700 to-zinc-900"><Package className="h-12 w-12 text-white/20" /></div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/5" />
      <div className="absolute inset-x-0 bottom-0 p-5">
        <h3 className="font-heading text-xl font-bold leading-tight text-white drop-shadow">{p.name}</h3>
        {p.description && <p className="mt-1 line-clamp-2 text-xs text-white/70">{p.description}</p>}
        <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-white/15 pt-3">
          <Stat label="RRP" value={money(p.rrp)} />
          <Stat label="Cost" value={money(p.cost)} />
          <Stat label="Profit" value={money(p.profit)} />
          <Stat label="Margin" value={p.margin != null ? `${p.margin.toFixed(1)}%` : "—"} accent={p.margin == null ? "text-white" : marginNeg ? "text-red-400" : "text-emerald-400"} />
        </div>
      </div>
    </div>
  );
}

export default function PublicPortfolio() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/product-shares/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setError("This portfolio link is invalid or has been removed."));
  }, [token]);

  if (error) return <div className="flex h-screen items-center justify-center bg-zinc-950 px-6 text-center text-sm text-zinc-400">{error}</div>;
  if (!data) return <div className="flex h-screen items-center justify-center bg-zinc-950"><Loader2 className="h-6 w-6 animate-spin text-[#e84e34]" /></div>;

  return (
    <div className="min-h-screen bg-zinc-950">
      <header className="border-b border-white/10 px-6 py-5 sm:px-10">
        <div className="mx-auto flex max-w-6xl items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center bg-[#e84e34] p-1.5">
            <img src={logoWhite} alt="Frdm BizOps" className="h-full w-auto object-contain" />
          </div>
          <div>
            <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-[#f3897a]">Frdm BizOps</p>
            <h1 className="font-heading text-xl font-bold tracking-tight text-white" data-testid="portfolio-title">{data.title}</h1>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-6 py-10 sm:px-10">
        {data.products.length === 0 ? (
          <p className="text-center text-sm text-zinc-500">No products in this portfolio.</p>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {data.products.map((p) => <ProductCard key={p.id} p={p} token={token} />)}
          </div>
        )}
        <p className="mt-10 text-center text-xs text-zinc-600">{data.products.length} product{data.products.length === 1 ? "" : "s"} · Powered by Frdm BizOps</p>
      </main>
    </div>
  );
}
